import { colourForEmission } from './pulseRenderColour';
import { PARTICLE_POOL_MAX } from './pulseRenderConstants';
import type { Particle, ParticleEmission } from './pulseRenderTypes';
import type { RenderQuality } from '../renderQuality';

/**
 * Particle pool + draw pass. Owns the live particle array (capped at
 * PARTICLE_POOL_MAX × the quality tier) and exposes spawn/update/draw
 * entry points the orchestrator wires into the main frame loop.
 *
 * Reduced-motion is applied at spawn time (count × 0.25, min 2), not at
 * update time, so the cost of the burst is also reduced — not just its
 * visual weight. The quality tier likewise scales the per-burst count
 * and gates the per-particle shadowBlur, which is the single biggest
 * cost on mobile (shadowBlur is rasterised through the compositor and
 * scales roughly linearly with active-particle count).
 */
export class ParticleSystem {
  private particles: Particle[] = [];
  private reducedMotion = false;
  private quality: RenderQuality;
  private particlePoolMax: number;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
    this.particlePoolMax = Math.round(PARTICLE_POOL_MAX * quality.particleScale);
  }

  emit(emission: ParticleEmission): void {
    const baseCount =
      emission.type === 'death'
        ? 36
        : emission.type === 'milestone'
          ? 30
          : emission.type === 'flip'
            ? 18
            : emission.type === 'hit_perfect'
              ? 16
              : emission.type === 'hit_good'
                ? 12
                : 10;
    const count = this.reducedMotion
      ? Math.max(2, Math.round(baseCount * 0.25))
      : this.scaleCount(baseCount);
    const colour = emission.colour ?? colourForEmission(emission.type);
    // Hard cap on the particle pool. Heavy bursts on a long song can
    // otherwise accumulate thousands of particles and tank the framerate,
    // especially on lower-end mobile devices.
    if (this.particles.length > this.particlePoolMax) {
      this.particles.splice(0, this.particles.length - this.particlePoolMax);
    }
    const speedBase =
      emission.type === 'death'
        ? 280
        : emission.type === 'milestone'
          ? 240
          : emission.type === 'flip'
            ? 200
            : 140;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = speedBase * (0.4 + Math.random());
      const life = 0.5 + Math.random() * 0.6;
      this.particles.push({
        x: emission.x,
        y: emission.y,
        vx: Math.cos(angle) * speed,
        vy:
          Math.sin(angle) * speed -
          (emission.type === 'hit_perfect' ? 60 : emission.type === 'milestone' ? 80 : 0),
        life,
        maxLife: life,
        colour,
        size: 2 + Math.random() * (emission.type === 'milestone' ? 4.5 : 3.5),
        type: emission.type,
      });
    }
  }

  private scaleCount(baseCount: number): number {
    if (this.quality.particleScale >= 1) {
      return baseCount;
    }
    return Math.max(1, Math.round(baseCount * this.quality.particleScale));
  }

  update(dt: number): void {
    if (this.particles.length === 0) {
      return;
    }
    for (let i = this.particles.length - 1; i >= 0; i -= 1) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += p.type === 'death' ? 320 * dt : 600 * dt;
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.particles.length === 0) {
      return;
    }
    ctx.save();
    const useShadows = this.quality.particleShadows && !this.reducedMotion;
    for (const p of this.particles) {
      const a = Math.max(0, p.life / p.maxLife);
      ctx.globalAlpha = a;
      if (useShadows) {
        ctx.shadowColor = p.colour;
        ctx.shadowBlur = 10;
      }
      ctx.fillStyle = p.colour;
      ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
    ctx.restore();
  }
}
